import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Feather, Loader2, BookOpen, Sparkles, Library } from "lucide-react";
import { toast } from "sonner";

export default function AuthPage() {
  const { login, register, formatApiErrorDetail } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", penName: "", email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password, remember);
      } else {
        await register(form.name, form.email, form.password, form.penName, remember);
      }
      toast.success(mode === "login" ? "Welcome back" : "Your writing studio is ready");
    } catch (err) {
      if (err.response?.data?.detail != null) {
        setError(formatApiErrorDetail(err.response.data.detail));
      } else if (err.code === "ECONNABORTED") {
        setError("That took too long to respond. Please check your connection and try again.");
      } else if (!err.response) {
        setError("Couldn't reach the server. Please check your connection and try again.");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left — brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-14 overflow-hidden bg-[#12100c]">
        <img
          src="https://images.unsplash.com/photo-1715304617193-a4f86af446bb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Author study"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12100c] via-[#12100c]/70 to-transparent" />
        <Link to="/" data-testid="auth-brand-home" className="relative z-10 flex items-center gap-3 text-primary-foreground w-fit">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center">
            <Feather className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="font-display text-3xl font-bold text-white">CodexStudio</span>
        </Link>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-5xl font-bold text-white leading-tight mb-6">
            Where stories keep their own memory.
          </h1>
          <p className="text-white/70 text-lg leading-relaxed mb-8 font-serif">
            Write your novel, and let the studio remember every character, weapon and world you create — automatically.
          </p>
          <div className="space-y-3 text-white/80">
            {[
              [Sparkles, "AI auto-detects vital story details as you write"],
              [BookOpen, "Organise projects into chapters with a Word-class editor"],
              [Library, "A living Storyboard Codex you can search & update"],
            ].map(([Icon, text], i) => (
              <div key={i} className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-primary" />
                <span className="text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-white/40 text-xs font-mono-code">EDITORIAL ATELIER · EST. 2026</div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <Link to="/" data-testid="auth-brand-home-mobile" className="lg:hidden flex items-center gap-3 mb-10 w-fit">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Feather className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display text-2xl font-bold">CodexStudio</span>
          </Link>

          <h2 className="font-display text-4xl font-bold mb-2">
            {mode === "login" ? "Welcome back" : "Begin your saga"}
          </h2>
          <p className="text-muted-foreground mb-8">
            {mode === "login" ? "Sign in to your writing studio." : "Create an account to start writing."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="auth-name-input" value={form.name} onChange={set("name")}
                    placeholder="Jane Doe" required className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="penName" className="flex items-center gap-2">
                    Pen name <span className="text-xs text-muted-foreground normal-case font-normal">(optional — used publicly)</span>
                  </Label>
                  <Input id="penName" data-testid="auth-penname-input" value={form.penName} onChange={set("penName")}
                    placeholder="e.g. J. Nightshade" className="h-11" />
                </div>
              </>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" value={form.email} onChange={set("email")}
                placeholder="you@example.com" required className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="auth-password-input" value={form.password}
                onChange={set("password")} placeholder="••••••••" required className="h-11" />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none pt-0.5" data-testid="auth-remember-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                data-testid="auth-remember-checkbox"
                className="w-4 h-4 rounded border-input accent-primary cursor-pointer" />
              <span className="text-sm text-muted-foreground">
                Keep me signed in <span className="text-xs">(uncheck on shared devices)</span>
              </span>
            </label>

            {error && <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>}

            <Button type="submit" data-testid="auth-login-submit-button" disabled={loading}
              className="w-full h-11 text-base font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === "login" ? "New to CodexStudio?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-primary font-semibold hover:underline">
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
